import { FeatureName } from '@bybeam/platform-types';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useForm, {
  ValidationMode,
  defaultRequiredErrorMessage,
  emailField,
  fieldUpdate,
  requiredField,
} from '@platform-ui-common/hooks/useForm/index.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import Button from '@platform-ui/common/components/Button/index.js';
import { useUserHomeRoute } from '@platform-ui/utilities/Routes.js';
import { checkPartnerHasFeature } from '@platform-ui/utilities/checkFeature.js';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import { getTenantConfig } from '../utils/getTenantConfig.js';

function SignInWithPassword(): JSX.Element {
  const partner = usePartner();
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { dispatch, formData, errors, trySubmit } = useForm(
    { email: sessionStorage.getItem('signinEmail') ?? '', password: '' },
    {
      email: emailField(),
      password: requiredField(undefined, defaultRequiredErrorMessage('Password')),
    },
    ValidationMode.OnSubmit,
  );

  const userHome = useUserHomeRoute();

  const {
    user,
    loading,
    identity: {
      auth,
      signinMethods: { basicAuth },
    },
    resetAuthState,
  } = useAuth();

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally running only on first render
  useEffect(() => {
    if (!auth?.currentUser) resetAuthState();
  }, []);

  useEffect(() => {
    if (user?.core && user?.gcip) navigate(userHome);
  });

  useEffect(() => {
    if (formData?.email) sessionStorage.setItem('signinEmail', formData.email);
  }, [formData]);

  const { email, password } = formData;
  const [authLoading, setAuthLoading] = useState(false);

  const signIn = () =>
    trySubmit(async () => {
      try {
        setAuthLoading(true);
        await basicAuth({ email, password });
      } catch (e) {
        setAuthLoading(false);
        setError(e);
      }
    });

  const { signin, roleRedirect, title } = getTenantConfig(partner);

  const hidePartnerPortalRedirect = checkPartnerHasFeature(
    partner,
    FeatureName.AuthenticationHidePartnerRedirect,
  );

  return (
    <FormContainer
      title={title}
      onSubmit={signIn}
      buttons={
        <div className="flex flex-col gap-8">
          <div className="w-full flex flex-col gap-y-8 items-center text-center">
            <ErrorDisplay errors={error} visible={!!error} message="Error signing in" />
            <Button loading={loading || authLoading} type="submit">
              Sign In
            </Button>
            <Button loading={loading || authLoading} variant="outline" link={signin}>
              Send me a link
            </Button>
          </div>
        </div>
      }
    >
      <TextInput
        id="email"
        label="Email Address"
        onChange={(value): void => dispatch(fieldUpdate('email', value))}
        value={email}
        error={errors.email as string}
        type="email"
        autoComplete="email"
        disabled={loading}
        required
      />
      <TextInput
        id="password"
        label="Password"
        onChange={(value): void => dispatch(fieldUpdate('password', value))}
        value={password}
        error={errors.password as string}
        type="password"
        autoComplete="current-password"
        disabled={loading}
        required
      />
      {hidePartnerPortalRedirect ? null : roleRedirect}
    </FormContainer>
  );
}

export default SignInWithPassword;
